import React from 'react';
import { Box, Theme, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { ChevronRight, AssignmentTurnedInOutlined, HowToRegOutlined, Warning } from '@material-ui/icons';
import Icon from '@mdi/react';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../global/config/useApiUrl';
import { useUser } from '../../../global/auth/useUser';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { TemplateSnackbar, SnackBarOptions } from '../../../common/snackbars/TemplateSnackbar';
import { iconList } from '../../../common/icons/RoleIconList';
import { ActivitiesTabs } from '../activities-v2/ActivitiesTabs';
import { ActivityContextValue } from '../activities-v2/ActivityContext';
import { bodyStyles } from '../activities-v2/ActivityStyles/shared';
import { GET_ROLES_SCREEN } from './RolesQuery.gql';
import { MyRoleStatusIndicator, RoleDisplayStatus, getRoleStatusColor } from './MyRoleStatusIndicator';
import { RolesColorLegend } from './RolesColorLegend';
import { RoleSidebar } from './RoleSidebar';
import { CollapsibleSideBar } from '../../../common/CollapsibleSideBar';
import { AlertCard } from '../../../common/cards/alert-card/AlertCard';

type RoleTaskRef = { taskId: string; title: string };

type RolesScreenRole = {
  activityRoleId: string;
  name: string;
  icon: string | null;
  description: string | null;
  canApply: boolean;
  applicationMode: string;
  allowReapply: boolean;
  tasks: RoleTaskRef[];
};

type RolesScreenRoleApplication = {
  roleApplicationId: string;
  status: string;
  dateApplied: string;
  dateActioned: string | null;
  firstApprovedAt: string | null;
  actionReason: string | null;
  role: RolesScreenRole;
};

type GetRolesScreen = {
  vm: {
    rolesAvailableForApplication: RolesScreenRole[];
    myRoleApplications: RolesScreenRoleApplication[];
    volunteer: {
      volunteerId: string;
      activityRoles: RolesScreenRole[];
    } | null;
    profile: {
      profileId: string;
      completedTasks: Array<{
        completedTaskId: string;
        task: { taskId: string };
      }>;
    } | null;
  };
};

const APPLICATION_MODE_LABEL = new Map<string, string>([
  ['auto', 'Auto Approval'],
  ['tasks_auto', 'Complete Tasks'],
  ['tasks_manual', 'Tasks & Review'],
  ['approve_tasks_manual', 'Review, Tasks & Approval'],
]);

const APPLICATION_STATUS_TO_DISPLAY = new Map<string, RoleDisplayStatus>([
  ['pending', 'pending'],
  ['in_progress', 'in_progress'],
  ['awaiting_review', 'awaiting_review'],
  ['approved', 'approved'],
]);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listIntro: {
      fontSize: 13,
      color: theme.color.grey.neutral200,
      marginTop: '1.25rem',
    },
    emptyState: {
      fontSize: 13,
      color: theme.color.grey.neutral200,
      marginTop: '1.25rem',
    },
    cardWrapper: {
      marginTop: '1.25rem',
    },
    card: {
      display: 'flex',
      width: '100%',
      borderRadius: '8px',
      boxSizing: 'border-box',
      overflow: 'hidden',
    },
    sideBar: {
      minWidth: '60px',
      width: '60px',
      padding: '0 16px',
      borderRadius: '8px 0px 0px 8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    bodyArea: {
      display: 'flex',
      flexGrow: 2,
      alignItems: 'center',
      padding: '16px 20px',
      overflow: 'hidden',
    },
    titleText: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    footerRow: {
      display: 'flex',
      alignItems: 'center',
      marginTop: '8px',
      flexWrap: 'wrap',
    },
    footerCell: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: 16,
    },
    footerIcon: {
      color: theme.color.grey.neutral200,
      fontSize: 14,
      marginRight: 6,
    },
    footerText: {
      color: theme.color.grey.neutral200,
      marginTop: 1,
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: 16,
    },
    tasksBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      backgroundColor: '#FFF4E0',
      color: '#A05C00',
      borderRadius: 12,
      padding: '2px 8px',
      marginRight: 16,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: '16px',
    },
    tasksBadgeIcon: {
      fontSize: 14,
      marginRight: 4,
    },
  })
);

function RoleSidebarIcon({ icon }: { icon: string | null }) {
  const path = icon ? (iconList as Record<string, string>)[icon] : undefined;
  if (!path) {
    return null;
  }
  return <Icon path={path} size={1} color='white' />;
}

function plainTextFromDescription(description: string | null): string {
  if (!description) {
    return '';
  }
  try {
    const parsed = JSON.parse(description);
    if (Array.isArray(parsed?.blocks)) {
      return parsed.blocks
        .map((block: { text?: string }) => block?.text ?? '')
        .filter(Boolean)
        .join(' ');
    }
  } catch (e) {
    return description;
  }
  return '';
}

interface RoleCardProps {
  role: RolesScreenRole;
  sidebarColor: string;
  action: React.ReactNode;
  onClick: () => void;
  completedTaskIds: Set<string>;
  showTaskProgress: boolean;
  needsTasksAttention: boolean;
}

function RoleCard({
  role,
  sidebarColor,
  action,
  onClick,
  completedTaskIds,
  showTaskProgress,
  needsTasksAttention,
}: RoleCardProps) {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const taskCount = role.tasks?.length ?? 0;
  const completedCount = (role.tasks ?? []).filter((t) => completedTaskIds.has(t.taskId)).length;
  const remainingCount = Math.max(taskCount - completedCount, 0);
  const descriptionPreview = plainTextFromDescription(role.description);
  const modeLabel = APPLICATION_MODE_LABEL.get(role.applicationMode);
  const taskLabel =
    showTaskProgress && taskCount > 0
      ? `${completedCount} of ${taskCount} ${taskCount === 1 ? 'task' : 'tasks'} complete`
      : `${taskCount} ${taskCount === 1 ? 'task' : 'tasks'}`;

  const onCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, [role="button"], [aria-haspopup]')) {
      return;
    }
    onClick();
  };

  return (
    <Box className={classes.cardWrapper}>
      <Box
        className={classes.card}
        border={`1px solid ${theme.color.grey.border}`}
        bgcolor={sidebarColor}
        onClick={onCardClick}
        style={{ cursor: 'pointer' }}
      >
        <Box className={classes.sideBar} bgcolor={sidebarColor}>
          <RoleSidebarIcon icon={role.icon} />
        </Box>
        <Box className={classes.bodyArea} bgcolor={theme.color.white.neutral50}>
          <Box flexGrow={1} overflow='hidden'>
            <Typography
              variant='h6'
              noWrap
              className={classes.titleText}
              style={{ color: theme.color.grey.neutral400, width: '100%' }}
            >
              {role.name}
            </Typography>
            {descriptionPreview && (
              <Typography
                variant='caption'
                noWrap
                style={{ color: theme.color.grey.neutral200, display: 'block', marginTop: 4 }}
              >
                {descriptionPreview}
              </Typography>
            )}
            <Box className={classes.footerRow}>
              {needsTasksAttention && remainingCount > 0 ? (
                <Box className={classes.tasksBadge}>
                  <Warning className={classes.tasksBadgeIcon} />
                  {remainingCount} {remainingCount === 1 ? 'task' : 'tasks'} to complete
                </Box>
              ) : (
                taskCount > 0 && (
                  <Box className={classes.footerCell}>
                    <AssignmentTurnedInOutlined className={classes.footerIcon} />
                    <Typography variant='caption' className={classes.footerText}>
                      {taskLabel}
                    </Typography>
                  </Box>
                )
              )}
              {modeLabel && (
                <Box className={classes.footerCell}>
                  <HowToRegOutlined className={classes.footerIcon} />
                  <Typography variant='caption' className={classes.footerText}>
                    {modeLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box className={classes.actions}>{action}</Box>
        </Box>
      </Box>
    </Box>
  );
}

type RoleListItem =
  | { kind: 'available'; role: RolesScreenRole }
  | { kind: 'application'; role: RolesScreenRole; application: RolesScreenRoleApplication }
  | { kind: 'held'; role: RolesScreenRole };

interface RolesScreenProps {}

export const RolesScreen: React.FC<RolesScreenProps> = () => {
  const bodyClasses = bodyStyles();
  const { theme } = useCampfireTheme();

  const { userIdentity, getVolunteerIdentity } = useUser();
  const { volunteerId } = getVolunteerIdentity();
  const { userId } = userIdentity;
  const apiUrl = useApiUrl();
  const [snackbar, setSnackbar] = React.useState<SnackBarOptions>();
  const [sidebarRole, setSidebarRole] = React.useState<RolesScreenRole | null>(null);

  const activityContext = React.useContext(ActivityContextValue);
  const activeTab = activityContext?.activeTab ?? 'roles';
  const setActiveTab = activityContext?.setActiveTab ?? (() => undefined);
  const { isMobile } = useCampfireTheme();

  const { data, loading, refetch } = useCampfireQuery<GetRolesScreen, { volunteerId: string; userId: string }>(
    GET_ROLES_SCREEN,
    {
      options: { variables: { volunteerId, userId } },
    }
  );

  const completedTaskIds = React.useMemo(() => {
    return new Set((data?.vm.profile?.completedTasks ?? []).map((ct) => ct.task.taskId));
  }, [data]);

  const campfireMutation = useCampfireFetch({ defer: true });

  const refresh = () => {
    if (refetch) {
      setTimeout(refetch, 0);
    }
  };

  const onApply = (role: RolesScreenRole) => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/role-applications/apply`,
        method: 'post',
        data: { activityRoleId: role.activityRoleId, userId },
      })
      .then((res: any) => {
        if (res?.data?.status >= 400 || res?.error) {
          setSnackbar({
            variant: 'error',
            open: true,
            message: res?.data?.message ?? 'Unable to apply for role',
          });
          return;
        }
        setSnackbar({ variant: 'success', open: true, message: `Applied for ${role.name}` });
        refresh();
      });
  };

  const onWithdraw = (application: RolesScreenRoleApplication) => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/role-applications/withdraw`,
        method: 'post',
        data: { roleApplicationId: application.roleApplicationId, userId },
      })
      .then(() => {
        setSnackbar({ variant: 'success', open: true, message: 'Application withdrawn' });
        refresh();
      });
  };

  const items: RoleListItem[] = React.useMemo(() => {
    if (!data) return [];

    const heldRoles = data.vm.volunteer?.activityRoles ?? [];
    const heldRoleIds = new Set(heldRoles.map((r) => r.activityRoleId));

    const openApplications = (data.vm.myRoleApplications ?? []).filter((ra) =>
      ['pending', 'in_progress', 'awaiting_review'].includes(ra.status)
    );
    const openRoleIds = new Set(openApplications.map((ra) => ra.role.activityRoleId));

    const result: RoleListItem[] = [];

    openApplications.forEach((application) => {
      result.push({ kind: 'application', role: application.role, application });
    });

    (data.vm.rolesAvailableForApplication ?? []).forEach((role) => {
      if (heldRoleIds.has(role.activityRoleId) || openRoleIds.has(role.activityRoleId)) return;
      result.push({ kind: 'available', role });
    });

    heldRoles.forEach((role) => {
      result.push({ kind: 'held', role });
    });

    return result;
  }, [data]);

  const renderAction = (item: RoleListItem) => {
    if (item.kind === 'available') {
      return (
        <TabletButton
          variant='outlined'
          color='primary'
          onClick={() => onApply(item.role)}
          endIcon={<ChevronRight style={{ color: theme.color.grey.neutral300, marginLeft: '-2px' }} />}
          style={{ padding: '8px 12px', border: '1px solid #DCDCDC', whiteSpace: 'nowrap' }}
        >
          <Box display='flex' alignItems='center'>
            <Box height='10px' width='10px' borderRadius='50%' marginRight='8px' bgcolor='#A7D6FC' />
            <Typography
              variant='caption'
              style={{
                fontSize: '12px',
                fontWeight: 700,
                color: theme.color.grey.neutral400,
                textTransform: 'uppercase',
                lineHeight: '14px',
                marginTop: '1px',
              }}
            >
              Apply
            </Typography>
          </Box>
        </TabletButton>
      );
    }
    if (item.kind === 'application') {
      return (
        <MyRoleStatusIndicator
          status={APPLICATION_STATUS_TO_DISPLAY.get(item.application.status) ?? 'pending'}
          actions={['withdraw']}
          onAction={() => onWithdraw(item.application)}
        />
      );
    }
    return <MyRoleStatusIndicator status='approved' actions={[]} onAction={() => undefined} />;
  };

  return (
    <Box display='flex' height='100%' overflow='hidden'>
      <Box className={bodyClasses.rootContainer}>
        <ActivitiesTabs activeTab={activeTab} onSelectTab={setActiveTab} />

        <Box className={bodyClasses.stickyBox}>
          <RolesColorLegend />
        </Box>

        {!loading && items.length === 0 ? (
          <Box marginTop='1rem'>
            <AlertCard variant='info' title='There are no roles to show' />
          </Box>
        ) : (
          items.map((item) => {
            const key =
              item.kind === 'application'
                ? item.application.roleApplicationId
                : `${item.kind}-${item.role.activityRoleId}`;
            const status: RoleDisplayStatus =
              item.kind === 'available'
                ? 'available'
                : item.kind === 'held'
                ? 'approved'
                : APPLICATION_STATUS_TO_DISPLAY.get(item.application.status) ?? 'pending';
            const showTaskProgress = item.kind === 'application' && item.role.tasks?.length > 0;
            const needsTasksAttention = item.kind === 'application' && item.application.status === 'in_progress';
            return (
              <RoleCard
                key={key}
                role={item.role}
                sidebarColor={getRoleStatusColor(status)}
                action={renderAction(item)}
                onClick={() => setSidebarRole(item.role)}
                completedTaskIds={completedTaskIds}
                showTaskProgress={showTaskProgress}
                needsTasksAttention={needsTasksAttention}
              />
            );
          })
        )}

        <Box pb='2rem' />

        {snackbar && <TemplateSnackbar {...snackbar} onClose={() => setSnackbar(undefined)} />}
      </Box>
      {!isMobile && sidebarRole && (
        <CollapsibleSideBar>
          <RoleSidebar
            role={sidebarRole}
            onClose={() => setSidebarRole(null)}
            completedTaskIds={completedTaskIds}
            onTaskCompleted={refresh}
          />
        </CollapsibleSideBar>
      )}
    </Box>
  );
};

export default RolesScreen;
